import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  ChevronLeft,
  Star,
  Package,
  Calendar,
  User,
  AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { getReviewById } from "../lib/api";
import type { ReviewWithRelations } from "../lib/types";
import { toast } from "sonner";

interface ReviewDetailProps {
  reviewId?: string;
  onNavigate?: (page: string, id?: string) => void;
}

export function ReviewDetail({ reviewId, onNavigate }: ReviewDetailProps) {
  const [review, setReview] = useState<ReviewWithRelations | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReview = async () => {
      if (!reviewId) {
        toast.error("Review ID is required");
        onNavigate && onNavigate("discover");
        return;
      }

      try {
        setLoading(true);
        const data = await getReviewById(reviewId);

        if (!data) {
          toast.error("Review not found");
          onNavigate && onNavigate("discover");
          return;
        }

        setReview(data);
      } catch (error: any) {
        console.error("Error fetching review:", error);
        toast.error("Failed to load review");
      } finally {
        setLoading(false);
      }
    };

    fetchReview();
  }, [reviewId, onNavigate]);

  const getGradeFromStars = (stars: number): string => {
    if (stars === 5) return "A+";
    if (stars === 4) return "B+";
    if (stars === 3) return "C+";
    if (stars === 2) return "D";
    return "F";
  };

  const getGradeColor = (grade: string) => {
    if (grade.startsWith("A")) return "text-primary";
    if (grade.startsWith("B")) return "text-blue-600";
    if (grade.startsWith("C")) return "text-yellow-600";
    return "text-muted-foreground";
  };

  const getGradientClass = (grade: string) => {
    if (grade.startsWith("A")) return "gradient-grade-a";
    if (grade.startsWith("B")) return "gradient-grade-b";
    if (grade.startsWith("C")) return "gradient-grade-c";
    return "bg-muted";
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-10 w-48 mb-6" />
          <div className="space-y-6">
            <Skeleton className="h-64 w-full rounded-2xl" />
            <Skeleton className="h-96 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!review) {
    return null;
  }

  const grade = getGradeFromStars(review.stars);

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            variant="ghost"
            className="mb-6 hover:bg-primary/10"
            onClick={() =>
              review.product && onNavigate && onNavigate("product-detail", review.product_id)
            }
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Product
          </Button>
        </motion.div>

        {/* Review Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="p-8 glass-card mb-6">
            <div className="flex flex-col md:flex-row items-start justify-between gap-6 mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`${getGradientClass(grade)} w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg`}>
                    <span className="text-2xl text-white">{grade}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < review.stars
                              ? "fill-current text-primary"
                              : "text-muted-foreground"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {review.stars} out of 5 stars
                    </p>
                  </div>
                </div>

                {review.title && (
                  <h1 className="text-2xl mb-2">{review.title}</h1>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Badge variant={review.mod_state === "approved" ? "default" : "secondary"}>
                  {review.mod_state}
                </Badge>
                {review.is_flagged && (
                  <Badge variant="outline" className="border-yellow-600 text-yellow-600">
                    Flagged
                  </Badge>
                )}
              </div>
            </div>

            {/* Review Meta Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 border-t border-b border-border/50">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Reviewed by</p>
                  <p className="font-medium">{review.author?.display_name || "Anonymous"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Submitted on</p>
                  <p className="font-medium">
                    {new Date(review.submitted_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {review.batch && (
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Batch</p>
                    <p className="font-medium">#{review.batch.batch_no}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Review Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          <Card className="p-8 glass-card mb-6">
            <h2 className="text-xl mb-4">Review</h2>
            {review.body ? (
              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {review.body}
              </p>
            ) : (
              <p className="text-muted-foreground italic">
                No detailed review text provided.
              </p>
            )}
          </Card>
        </motion.div>

        {/* Product Info */}
        {review.product && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Card className="p-6 glass-card mb-6">
              <h3 className="text-lg mb-4">Product Information</h3>
              
              <div
                className="p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => onNavigate && onNavigate("product-detail", review.product_id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-medium mb-1">{review.product.product_name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {review.product.brand || "Various Brands"}
                    </p>
                  </div>
                  <Badge className="capitalize">{review.product.form}</Badge>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-4">
                  <div className="text-center p-2 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">THC</p>
                    <p className="text-sm font-medium">
                      {review.product.thc_pct ? `${review.product.thc_pct}%` : "N/A"}
                    </p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">CBD</p>
                    <p className="text-sm font-medium">
                      {review.product.cbd_pct ? `${review.product.cbd_pct}%` : "N/A"}
                    </p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">Pack</p>
                    <p className="text-sm font-medium">{review.product.pack_size || "N/A"}</p>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <Card className="p-6 glass-card">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm mb-2">Disclaimer</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  This review reflects an individual patient's experience and should not be
                  considered medical advice. Results may vary. Always consult with your healthcare
                  provider before use.
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
